<template>
  <div class="page-container">
    <PageHeader title="客户群查询">
      <template #actions>
        <ArchiveAppSelect v-model="currentApp" @change="handleAppChange" />
      </template>
    </PageHeader>

    <!-- 未选择接入方时的空态引导（EmptyState 走全局 .qw-empty 体系） -->
    <div v-if="!currentApp" class="qw-group-list__placeholder">
      <TablePanel>
        <EmptyState
          icon="el-icon-connection"
          title="请先选择接入方"
          description="会话存档接口需以接入方身份调用（携带 appId/appSecret），请从右上角选择"
        />
      </TablePanel>
    </div>

    <template v-else>
      <SearchPanel>
        <el-input v-model.trim="query.name" size="small" placeholder="群名（模糊匹配）" clearable @keyup.enter.native="handleSearch" />
        <el-input v-model.trim="query.owner" size="small" placeholder="群主 userid" clearable @keyup.enter.native="handleSearch" />
        <template #actions>
          <el-button type="primary" size="small" icon="el-icon-search" :loading="loading" @click="handleSearch">查 询</el-button>
          <el-button size="small" icon="el-icon-refresh-left" @click="handleReset">重 置</el-button>
        </template>
      </SearchPanel>

      <TablePanel>
        <el-table v-loading="loading" :data="groupList" stripe>
          <el-table-column prop="name" label="群名称" min-width="200" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.name || '未获取到群名' }}</template>
          </el-table-column>
          <el-table-column label="群 chatId" min-width="220">
            <template slot-scope="{ row }">
              <div class="qw-group-list__chat-id">
                <span class="qw-copy-text qw-text-ellipsis">{{ row.chatId }}</span>
                <el-button type="text" size="mini" icon="el-icon-document-copy" @click="handleCopy(row.chatId)" />
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="owner" label="群主 userid" min-width="130">
            <template slot-scope="{ row }">{{ row.owner || '-' }}</template>
          </el-table-column>
          <el-table-column prop="memberCount" label="成员数" width="85" align="center" />
          <el-table-column prop="externalCount" label="外部成员" width="90" align="center" />
          <el-table-column label="建群时间" min-width="165">
            <template slot-scope="{ row }">{{ formatDateTime(row.groupCreateTime) }}</template>
          </el-table-column>
          <el-table-column label="同步时间" min-width="165">
            <template slot-scope="{ row }">{{ formatDateTime(row.syncTime) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100" align="center" class-name="aj-table-cell">
            <template slot-scope="{ row }">
              <el-link class="aj-table-operation-link aj-table-operation-link--primary" :underline="false" @click="openDetail(row)">详情</el-link>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :current-page.sync="query.currentPage"
          :page-size.sync="query.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          @current-change="fetchGroupList"
          @size-change="handleSearch"
        />
      </TablePanel>
    </template>

    <!-- 群详情弹窗：基本信息 + 成员列表（rawJson 中的 member_list） -->
    <el-dialog title="客户群详情" :visible.sync="detailVisible" width="760px" top="6vh">
      <div v-loading="detailLoading">
        <template v-if="detailGroup">
          <el-descriptions :column="2" size="small" border class="qw-group-list__desc">
            <el-descriptions-item label="群名称">{{ detailGroup.name || '-' }}</el-descriptions-item>
            <el-descriptions-item label="群主 userid">{{ detailGroup.owner || '-' }}</el-descriptions-item>
            <el-descriptions-item label="chatId" :span="2">
              <span class="qw-copy-text">{{ detailGroup.chatId }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="成员数">{{ detailGroup.memberCount }}</el-descriptions-item>
            <el-descriptions-item label="外部成员数">{{ detailGroup.externalCount }}</el-descriptions-item>
            <el-descriptions-item label="建群时间">{{ formatDateTime(detailGroup.groupCreateTime) }}</el-descriptions-item>
            <el-descriptions-item label="同步时间">{{ formatDateTime(detailGroup.syncTime) }}</el-descriptions-item>
          </el-descriptions>

          <div class="qw-group-list__members-title">群成员（{{ detailMembers.length }}）</div>
          <el-table :data="detailMembers" size="small" max-height="320" border>
            <el-table-column prop="userid" label="成员 userid" min-width="180" show-overflow-tooltip />
            <el-table-column label="类型" width="110" align="center">
              <template slot-scope="{ row }">
                <el-tag size="mini" :type="row.type === 1 ? '' : 'warning'">
                  {{ row.type === 1 ? '企业成员' : (row.type === 2 ? '外部联系人' : row.type) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="groupNickname" label="群昵称" min-width="150" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ row.groupNickname || '-' }}</template>
            </el-table-column>
            <el-table-column label="入群时间" min-width="160">
              <template slot-scope="{ row }">{{ formatDateTime(row.joinTime) }}</template>
            </el-table-column>
          </el-table>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import EmptyState from '@/components/EmptyState.vue'
import ArchiveAppSelect from '@/components/ArchiveAppSelect.vue'
import { getGroupList, getGroupDetail } from '@/api/archive'
import { formatDateTime, copyText } from '@/utils/format'

// 客户群查询：分页列表 + 群详情（成员列表），以接入方身份调 /qw-archive/group/*
export default {
    name: 'GroupList',
    components: { PageHeader, SearchPanel, TablePanel, EmptyState, ArchiveAppSelect },
    data () {
        return {
            currentApp: null,
            loading: false,
            groupList: [],
            total: 0,
            query: {
                name: '',
                owner: '',
                currentPage: 1,
                pageSize: 20
            },
            detailVisible: false,
            detailLoading: false,
            detailGroup: null,
            detailMembers: []
        }
    },
    methods: {
        formatDateTime,
        handleAppChange () {
            // 切换接入方后重新查询第一页
            this.query.currentPage = 1
            if (this.currentApp) {
                this.fetchGroupList()
            } else {
                this.groupList = []
                this.total = 0
            }
        },
        async fetchGroupList () {
            if (!this.currentApp) return
            this.loading = true
            try {
                const res = await getGroupList(this.query, this.currentApp)
                this.groupList = res.data || []
                this.total = Number(res.total || 0)
            } catch (error) {
                // 错误提示已由 request 拦截器统一弹出
            } finally {
                this.loading = false
            }
        },
        handleSearch () {
            this.query.currentPage = 1
            this.fetchGroupList()
        },
        handleReset () {
            this.query = { name: '', owner: '', currentPage: 1, pageSize: this.query.pageSize }
            this.fetchGroupList()
        },
        async handleCopy (text) {
            try {
                await copyText(text)
                this.$message.success('已复制到剪贴板')
            } catch (error) {
                this.$message.error('复制失败，请手动复制')
            }
        },
        // 群详情：基本信息 + 成员（type 1 企业成员 / 2 外部联系人）
        async openDetail (row) {
            this.detailVisible = true
            this.detailLoading = true
            this.detailGroup = null
            this.detailMembers = []
            try {
                const res = await getGroupDetail(row.chatId, this.currentApp)
                const data = res.data || {}
                this.detailGroup = data.group || null
                this.detailMembers = data.members || []
            } catch (error) {
                // 错误提示已由 request 拦截器统一弹出
            } finally {
                this.detailLoading = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.qw-group-list__chat-id {
    display: flex;
    align-items: center;
    gap: 4px;

    span {
        flex: 1;
        min-width: 0;
    }
}

.qw-group-list__desc {
    margin-bottom: 16px;
}

// 成员列表小节标题：左侧渐变装饰条对齐全局面板标题形态
.qw-group-list__members-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--qw-text-primary);
    margin: 4px 0 10px;

    &::before {
        content: '';
        width: 3px;
        height: 14px;
        border-radius: 2px;
        background: linear-gradient(180deg, #2563eb, #06b6d4);
    }
}
</style>
